import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MilestoneCard } from './MilestoneCard';

interface ProgressSectionProps {
  milestones: any[];
  user: any;
  patientMetadata: any;
  userPrograms: string[];
  userGroups: string[];
  canAddMilestone: (user: any, patient: any, programs: string[], groups: string[]) => boolean;
  canEditMilestone: (user: any, patient: any, programs: string[], groups: string[]) => boolean;
  setShowAddMilestoneModal: (show: boolean) => void;
  handleEditMilestone: (milestone: any) => void;
  handleDragStart: (milestone: any) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, axis: 'emotional' | 'occupational' | 'rights') => void;
  draggedMilestone: any;
  handleMilestoneSelect: (id: string, checked: boolean) => void;
}

export function ProgressSection({
  milestones,
  user,
  patientMetadata,
  userPrograms,
  userGroups,
  canAddMilestone,
  canEditMilestone,
  setShowAddMilestoneModal,
  handleEditMilestone,
  handleDragStart,
  handleDragOver,
  handleDrop,
  draggedMilestone,
  handleMilestoneSelect
}: ProgressSectionProps) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="tab-panel">
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        gap: isMobile ? '12px' : '0',
        marginBottom: '20px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 className="form-block-title" style={{ color: '#000000', margin: 0 }}>🏥 {t('patientDetail.progressMilestones')}</h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
          {canAddMilestone(user, patientMetadata, userPrograms, userGroups) && (
          <button
            onClick={() => setShowAddMilestoneModal(true)}
            style={{
              background: '#007acc',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
          >
            {t('patientDetail.addMilestoneButton')}
          </button>
          )}
        </div>
      </div>

      {/* Responsive Column Layout - 3 columns on desktop, 1 column on mobile */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', 
        gap: '12px', 
        marginBottom: '20px',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Emotional Column */}
        <div style={{ minWidth: 0, overflow: 'hidden', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <h4 style={{ 
            textAlign: 'center', 
            padding: '10px', 
            background: '#6c757d', 
            color: 'white', 
            borderRadius: '8px 8px 0 0',
            margin: '0 0 0 0',
            fontSize: '15px',
            fontWeight: 'bold',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            רגשי
          </h4>
          <div 
            style={{ 
              border: '1px solid #6c757d', 
              borderTop: 'none', 
              borderRadius: '0 0 8px 8px', 
              minHeight: isMobile ? '200px' : '400px', 
              padding: '12px',
              background: '#fafafa',
              transition: 'background-color 0.2s ease',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, 'emotional')}
            onDragEnter={(e) => {
              e.preventDefault();
              if (draggedMilestone && draggedMilestone.axis !== 'emotional') {
                e.currentTarget.style.backgroundColor = '#e9ecef';
              }
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = '#fafafa';
            }}
          >
            {milestones.filter(m => m.axis === 'emotional').map((milestone) => (
              <MilestoneCard 
                key={milestone.id} 
                milestone={milestone} 
                onEdit={handleEditMilestone}
                onDragStart={handleDragStart}
                isSelected={milestone.status === 'achieved'}
                onSelect={(checked) => handleMilestoneSelect(milestone.id, checked)}
                canEdit={canEditMilestone(user, patientMetadata, userPrograms, userGroups)}
                user={user}
              />
            ))}
            {milestones.filter(m => m.axis === 'emotional').length === 0 && (
              <div style={{ textAlign: 'center', color: '#666', padding: '40px 20px' }}>
                אין יעדים רגשיים
              </div>
            )}
          </div>
        </div>

        {/* Occupational Column */}
        <div style={{ minWidth: 0, overflow: 'hidden', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <h4 style={{ 
            textAlign: 'center', 
            padding: '10px', 
            background: '#495057', 
            color: 'white', 
            borderRadius: '8px 8px 0 0',
            margin: '0 0 0 0',
            fontSize: '15px',
            fontWeight: 'bold',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            תעסוקתי
          </h4>
          <div 
            style={{ 
              border: '1px solid #495057', 
              borderTop: 'none', 
              borderRadius: '0 0 8px 8px', 
              minHeight: isMobile ? '200px' : '400px', 
              padding: '12px',
              background: '#fafafa',
              transition: 'background-color 0.2s ease',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, 'occupational')}
            onDragEnter={(e) => {
              e.preventDefault();
              if (draggedMilestone && draggedMilestone.axis !== 'occupational') {
                e.currentTarget.style.backgroundColor = '#e9ecef';
              }
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = '#fafafa';
            }}
          >
            {milestones.filter(m => m.axis === 'occupational').map((milestone) => (
              <MilestoneCard 
                key={milestone.id} 
                milestone={milestone} 
                onEdit={handleEditMilestone}
                onDragStart={handleDragStart}
                isSelected={milestone.status === 'achieved'}
                onSelect={(checked) => handleMilestoneSelect(milestone.id, checked)}
                canEdit={canEditMilestone(user, patientMetadata, userPrograms, userGroups)}
                user={user}
              />
            ))}
            {milestones.filter(m => m.axis === 'occupational').length === 0 && (
              <div style={{ textAlign: 'center', color: '#666', padding: '40px 20px' }}>
                אין יעדים תעסוקתיים
              </div>
            )}
          </div>
        </div>

        {/* Rights Column */}
        <div style={{ minWidth: 0, overflow: 'hidden', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <h4 style={{ 
            textAlign: 'center', 
            padding: '10px', 
            background: '#5a6268', 
            color: 'white', 
            borderRadius: '8px 8px 0 0',
            margin: '0 0 0 0',
            fontSize: '15px',
            fontWeight: 'bold',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            זכויות
          </h4>
          <div 
            style={{ 
              border: '1px solid #5a6268', 
              borderTop: 'none', 
              borderRadius: '0 0 8px 8px', 
              minHeight: isMobile ? '200px' : '400px', 
              padding: '12px',
              background: '#fafafa',
              transition: 'background-color 0.2s ease',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, 'rights')}
            onDragEnter={(e) => {
              e.preventDefault();
              if (draggedMilestone && draggedMilestone.axis !== 'rights') {
                e.currentTarget.style.backgroundColor = '#e9ecef';
              }
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = '#fafafa';
            }}
          >
            {milestones.filter(m => m.axis === 'rights').map((milestone) => (
              <MilestoneCard 
                key={milestone.id} 
                milestone={milestone} 
                onEdit={handleEditMilestone}
                onDragStart={handleDragStart}
                isSelected={milestone.status === 'achieved'}
                onSelect={(checked) => handleMilestoneSelect(milestone.id, checked)}
                canEdit={canEditMilestone(user, patientMetadata, userPrograms, userGroups)}
                user={user}
              />
            ))}
            {milestones.filter(m => m.axis === 'rights').length === 0 && (
              <div style={{ textAlign: 'center', color: '#666', padding: '40px 20px' }}>
                אין יעדים בעמודת זכויות
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legacy milestones without axis */}
      {milestones.filter(m => !m.axis).length > 0 && (
        <div style={{ marginTop: '30px' }}>
          <h4 style={{ color: '#666', marginBottom: '15px' }}>יעדים ללא ציר</h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {milestones.filter(m => !m.axis).map((milestone) => (
              <div
                key={milestone.id}
                style={{
                  background: 'white',
                  border: '2px solid #e9ecef',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onClick={() => handleEditMilestone(milestone)}
              >
                <h4 style={{ color: '#000000', marginBottom: '8px' }}>{milestone.title}</h4>
                <p style={{ color: '#666', marginBottom: '12px' }}>{milestone.description}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#007bff' }}>Progress: {milestone.progress}%</span>
                  <span style={{ color: '#28a745' }}>Status: {milestone.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

